import { KyInstance, type Options } from 'ky';
import { Uuid, UuidString } from '~src/utils/UUID';
import { Post, PostArray, ReactionType } from '~/src/types/posts';
import { z } from 'zod';

const getPostsOptionsDefaults: GetPostsOptions = {
  includeRecentComments: false,
};

export class PostsClient {
  private http_client: KyInstance;
  constructor(http_client: KyInstance) {
    this.http_client = http_client;
  }

  async get_posts(options: Partial<GetPostsOptions> = {}, config: Options = {}): Promise<Post[]> {
    const opts: GetPostsOptions = {
      ...getPostsOptionsDefaults,
      ...options,
    };

    // todo: handle other options as well
    const res = (await this.http_client
      .get('posts', {
        searchParams: { include_recent_comments: opts.includeRecentComments, direction: 'backwards' },
        ...config,
      })
      .json()) as { posts: PostArray };

    const posts = PostArray.parse(res.posts);
    return posts;
  }

  async create_post(options: CreatePostOptions, config: Options = {}) {
    const opts = {
      ...options,
      to_groups: Array.from(options.to_groups),
      media: Array.from(options.media),
      mentioned_users: Array.from(options.mentioned_users),
    };

    const res = await this.http_client.post('posts', { json: opts, ...config }).json();

    const post = CreatePostResponse.parse(res);

    return post.id;
  }

  async add_reaction(
    post_id: UuidString,
    reaction_type: ReactionType,
    config: Options = {},
  ) {
    await this.http_client.post('reactions/post', {
      json: { post_id, reaction_type },
      ...config,
    });
  }

  async delete_post(post_id: UuidString, config: Options = {}) {
    await this.http_client.delete(`posts/${post_id}`, config);
  }

  async remove_reaction(post_id: UuidString, config: Options = {}) {
    await this.http_client.delete('reactions/post', {
      searchParams: { post_id: post_id.toString() },
      ...config,
    });
  }
}

interface GetPostsOptions {
  includeRecentComments: boolean;
  direction?: 'forwards' | 'backwards';
  limit?: number;
  group_id?: Uuid;
}

interface CreatePostOptions {
  text_content: String;
  mentioned_users: Set<UuidString>;
  media: Set<UuidString>;
  to_groups: Set<UuidString>;
}

const CreatePostResponse = z.object({
  id: z
    .string()
    .uuid()
    .transform((u) => Uuid.from_string(u)),
});
type CreatePostResponse = z.infer<typeof CreatePostResponse>;
